<template>
  <FlotoContentLoader classes="h-100" :loading="loading">
    <DetailView
      v-if="movement.id"
      :default-sidebar-visible="isPage"
      :is-approval="isApproval"
      :approval="approval"
      :resource="movement"
      :disabled="isMovementCompleted || isMovementCancelled"
      :additional-info-col-size="isPage ? 9 : 12"
      :is-draft="isDraft"
      :is-transfer-received="isTransferReceived"
      :update-api="update"
      v-bind="{ ...$attrs }"
    >
      <template v-if="isPage" v-slot:before-title>
        <FlotoBackButton
          :to="
            backLink ||
            $modules.getModuleRoute('asset-management', 'asset-movement')
          "
          class="text-neutral-light mr-2"
        />
      </template>
      <template v-slot:actions>
        <slot name="actions">
          <slot name="action-before" />
          <MButton
            v-if="showCompleteAssetMovementButton"
            id="complete-btn"
            class="ml-2"
            @click="handleCompleteMovement"
          >
            {{ $t('complete') }} {{ $tc(moduleName) }}
          </MButton>
          <ActionMenu
            v-if="checkMovementActionMenu"
            :movement="movement"
            :module-name="moduleName"
            :disabled="disabled"
            :update-api="update"
            :requested-status-id="requestedStatusId"
            :requested-return-status-id="requestedReturnStatusId"
            :completed-status-id="completedStatusId"
            :cancelled-status-id="cancelledStatusId"
            :transfer-stage="transferStage"
            @archive="handleArchiveMovement"
            @update="update"
            @refresh="refreshMovement"
            @approval-changed="refreshMovement(['approval'])"
          />
          <slot name="action-after" />
        </slot>
      </template>
      <!-- default content as tabs -->
      <slot :ticket="movement">
        <template v-if="movement.movementType === 'asset_transfer'">
          <MDivider />
          <AssetTransferStagesContainer
            :module-name="moduleName"
            :resource="movement"
            :update-api="update"
            :completed-status-id="completedStatusId"
            :cancelled-status-id="cancelledStatusId"
            @refresh="refreshMovement"
          />
          <MDivider />
        </template>
        <div :key="tabsRerenderCount" class="tabs mt-6">
          <MAffix>
            <MTab v-model="activeTabKey" size="small" class="affixed-tab">
              <template v-for="tab in allowedTabs">
                <MTabPane v-if="!tab.disabled" :key="tab.id" :tab="tab.name" />
              </template>
            </MTab>
          </MAffix>
          <div class="my-4">
            <component
              :is="tabComponent"
              :disabled="
                tabComponentProps.alwaysEditable
                  ? false
                  : disabled || tabComponentProps.disabled
              "
              :resource-id="movement.id"
              :module-name="
                tabComponentProps.moduleName
                  ? tabComponentProps.moduleName
                  : moduleName
              "
              :resource="movement"
              v-bind="tabComponentProps"
              :update-api="update"
              :is-approval="isApproval"
              @refresh-page="refreshPage"
            />
          </div>
        </div>
      </slot>
    </DetailView>
    <!-- confirm modal for if ticket has secondarytickets and it gets closed -->
    <ConfirmCompleteModal
      v-if="showConfirmCompleteModal"
      :module-name="moduleName"
      @confirm="update({ statusId: completedStatusId }, true)"
      @hide="showConfirmCompleteModal = false"
    >
      <template v-slot:message>
        {{
          $t('confirm_complete', {
            resource: $tc(moduleName),
          })
        }}
      </template>
    </ConfirmCompleteModal>
  </FlotoContentLoader>
</template>
<script>
import { StatusComputed } from '@state/modules/status'
import {
  getMovementApi,
  updateMovementApi,
} from '@modules/asset-management/api/asset-movement-api'
import NoteContainer from '@components/notes/notes-container'
import ApprovalContainer from '@components/approval/approval-container'
import AuditContainer from '@components/audit/audit-container'
import AssetTransferStagesContainer from './asset-transfer-stages-container/asset-transfer-stages-container'
import ActionMenu from '../components/asset-movement-detail/action-menu'
import DetailView from '../components/asset-movement-detail/detail'
import AssetsTab from '../components/asset-movement/asset-list'
import ConfirmCompleteModal from '../components/asset-movement-detail/confirm-complete-modal'
import VendorTab from '../components/asset-movement-detail/asset-movement-tabs/vendor-tab'
export default {
  name: 'AssetMovementDetail',
  components: {
    DetailView,
    AssetsTab,
    VendorTab,
    NoteContainer,
    ApprovalContainer,
    AuditContainer,
    ActionMenu,
    ConfirmCompleteModal,
    AssetTransferStagesContainer,
  },
  props: {
    isPage: { type: Boolean, default: false },
    moduleName: { type: String, default: undefined },
    resourceId: { type: [String, Number], required: true },
    disabled: { type: Boolean, default: false },
    backLink: { type: Object, default: undefined },
    isApproval: { type: Boolean, default: false },
    approval: { type: Object, default: undefined },
    approvalAskOnStatusId: { type: [Number, String], default: undefined },
  },
  data() {
    return {
      tabsRerenderCount: 1,
      activeTabKey: 'assets',
      movement: {},
      loading: true,
      showConfirmCompleteModal: false,
      // transferStage: undefined,
    }
  },
  computed: {
    ...StatusComputed,
    transferStage() {
      if (this.movement.movementType === 'asset_transfer') {
        return (
          this[`${this.moduleName}FindStatusFromId`](this.movement.statusId) ||
          {}
        ).stage
      }
      return undefined
    },
    inDraftStatusId() {
      if (this[`${this.moduleName}FindStatus`]) {
        if (this.movement.movementType === 'asset_transfer') {
          return (
            this[`${this.moduleName}FindStatus`](
              'in draft',
              'request_transfer'
            ) || {}
          ).id
        }
        return 0
      }
      return undefined
    },
    requestedStatusId() {
      if (this[`${this.moduleName}FindStatus`]) {
        if (this.movement.movementType === 'asset_transfer') {
          return (
            this[`${this.moduleName}FindStatus`](
              'requested',
              'request_transfer'
            ) || {}
          ).id
        }
        return (this[`${this.moduleName}FindStatus`]('requested') || {}).id
      }
      return undefined
    },
    requestedReturnStatusId() {
      if (this[`${this.moduleName}FindStatus`]) {
        if (this.movement.movementType === 'asset_transfer') {
          return (
            this[`${this.moduleName}FindStatus`](
              'requested',
              'request_return'
            ) || {}
          ).id
        }
        return (this[`${this.moduleName}FindStatus`]('requested') || {}).id
      }
      return undefined
    },
    completedStatusId() {
      if (this[`${this.moduleName}FindStatus`]) {
        if (this.movement.movementType === 'asset_transfer') {
          return (
            this[`${this.moduleName}FindStatus`]('completed', 'complete') || {}
          ).id
        }
        return (this[`${this.moduleName}FindStatus`]('completed') || {}).id
      }
      return undefined
    },
    cancelledStatusId() {
      if (this[`${this.moduleName}FindStatus`]) {
        return (this[`${this.moduleName}FindStatus`]('cancelled') || {}).id
      }
      return undefined
    },
    transferReceivedStatusId() {
      if (this[`${this.moduleName}FindStatus`]) {
        if (this.movement.movementType === 'asset_transfer') {
          return (
            this[`${this.moduleName}FindStatus`](
              'received',
              'receive_transfer'
            ) || {}
          ).id
        }
      }
      return undefined
    },
    returnReceivedStatusId() {
      if (this[`${this.moduleName}FindStatus`]) {
        if (this.movement.movementType === 'asset_transfer') {
          return (
            this[`${this.moduleName}FindStatus`](
              'received',
              'receive_return'
            ) || {}
          ).id
        }
      }
      return undefined
    },
    showCompleteAssetMovementButton() {
      if (this.movement.movementType === 'asset_transfer') {
        return !this.isMovementCompleted && this.transferStage === 'complete'
      } else {
        return (
          this.movement.statusId !== this.completedStatusId &&
          this.movement.statusId !== this.cancelledStatusId &&
          (this.movement.approvalStatus === 'approved' ||
            this.movement.approvalStatus === 'pre_approved')
        )
      }
      // return false
    },
    checkMovementActionMenu() {
      if (
        (this.movement.statusId === this.requestedStatusId &&
          this.movement.approvalStatus === 'not_requested') ||
        this.movement.statusId === this.requestedStatusId ||
        (this.movement.statusId !== this.cancelledStatusId &&
          (this.movement.approvalStatus === 'approved' ||
            this.movement.approvalStatus === 'pre_approved')) ||
        ['transfer_approval', 'return_approval'].indexOf(this.transferStage) >=
          0
      ) {
        return true
      }
      return false
    },
    isTransferReceived() {
      return this.movement.statusId >= this.transferReceivedStatusId
    },
    isMovementCompleted() {
      return this.disabled || this.movement.statusId === this.completedStatusId
    },
    isMovementCancelled() {
      return this.disabled || this.movement.statusId === this.cancelledStatusId
    },
    isDraft() {
      return this.disabled || this.movement.statusId === this.inDraftStatusId
    },
    hiddenColumnsForDetail() {
      let column = []
      if (
        this.movement &&
        this.movement.movementType === 'return_from_repair'
      ) {
        column = [
          'statusId',
          'inWarranty',
          'referenceNumber',
          'expectedDate',
          // 'assetCondition',
          'remarks',
        ]
      } else if (
        this.movement &&
        ['send_to_repair', 'asset_transfer'].indexOf(
          this.movement.movementType
        ) >= 0
      ) {
        column = [
          'statusId',
          'referenceNumber',
          'expectedDate',
          'assetCondition',
        ]
      }
      return column
    },
    tabComponent() {
      return (this.allowedTabs.find((t) => t.id === this.activeTabKey) || {})
        .component
    },
    tabComponentProps() {
      return (
        (this.allowedTabs.find((t) => t.id === this.activeTabKey) || {})
          .componentProps || {}
      )
    },
  },
  watch: {
    resourceId: {
      immediate: true,
      handler: 'getMovement',
    },
    hiddenColumnsForDetail: {
      immediate: true,
      handler: 'setTabs',
    },
  },
  created() {
    this.setTabs()
  },
  methods: {
    handleDelete(item) {
      const ids = (this.movement.assetIds || []).filter((i) => i !== item.id)
      return this.update({ assetIds: ids })
    },
    setTabs() {
      let tabs = [
        {
          id: 'assets',
          name: `${this.$tc('asset', 2)}`,
          component: AssetsTab,
          componentProps: {
            hiddenColumns: this.hiddenColumnsForDetail,
            hasActions: true,
            showEdit: this.movement.movementType !== 'asset_transfer',
            disabled: true,
            deleteFn: this.handleDelete,
          },
        },
        {
          id: 'vendor',
          name:
            this.movement.movementType === 'asset_transfer'
              ? `${this.$tc('address')}`
              : `${this.$tc('vendor')}`,
          component: VendorTab,
        },
        {
          id: 'note',
          name: this.$tc('note', 2),
          component: NoteContainer,
          componentProps: {
            alwaysEditable: true,
            stickySearch: true,
            offsetTop: 38,
          },
        },
        {
          id: 'approval',
          name: this.$tc('approval', 2),
          component: ApprovalContainer,
        },
        {
          id: 'activity',
          name: this.$tc('audit_trail'),
          component: AuditContainer,
        },
      ]
      if (
        !this.movement.approvalStatus ||
        this.movement.approvalStatus === 'pre_approved' ||
        this.movement.approvalStatus === 'not_requested'
      ) {
        tabs = tabs.filter((t) => t.id !== 'approval')
      }
      this.allowedTabs = Object.freeze(tabs)
    },
    update(change, forceUpdate = false) {
      if (Object.keys(change).length) {
        if (
          forceUpdate !== true &&
          'statusId' in change &&
          change.statusId === this.completedStatusId
        ) {
          this.showConfirmCompleteModal = true
          return Promise.resolve()
        }
        return updateMovementApi({
          id: this.movement.id,
          ...change,
        }).then((data) => {
          this.movement = { ...this.movement, ...data }
          if (this.onChange) {
            this.onChange(data)
          }
          return data
        })
      }
      return this.refreshMovement(['approval'])
    },
    refreshPage() {
      this.activeTabKey = 'assets'
      this.refreshMovement()
    },
    refreshMovement(reRenderTabs = undefined) {
      return getMovementApi(this.resourceId).then((movement) => {
        this.movement = movement
        if (reRenderTabs && reRenderTabs.indexOf(this.activeTabKey) >= 0) {
          this.tabsRerenderCount = this.tabsRerenderCount + 1
        }
        this.setTabs()
        if (
          reRenderTabs &&
          reRenderTabs.indexOf('approval') >= 0 &&
          this.allowedTabs.find((t) => t.id === 'approval') &&
          this.activeTabKey !== 'approval'
          // movement.approvalStatus !== 'pre_approved'
        ) {
          this.activeTabKey = 'approval'
          // this.$nextTick(() => {
          //   this.setTabs()
          // })
        }
        return movement
      })
    },
    handleCompleteMovement() {
      this.update({ statusId: this.completedStatusId })
    },
    getMovement() {
      this.loading = true
      this.movement = {}
      return getMovementApi(this.resourceId)
        .then((movement) => {
          this.loading = false
          this.movement = {
            ...movement,
            approvalAskOnStatusId: this.approvalAskOnStatusId,
          }
          this.$emit('resource', {
            ...movement,
            approvalAskOnStatusId: this.approvalAskOnStatusId,
          })
          // this.setTabs()
          return movement
        })
        .catch((e) => {
          this.error = e
          this.loading = false
          this.$router.replace(this.$currentModule.getRoute(''))
        })
    },
    handleArchiveMovement() {},
  },
}
</script>
